import React from "react";
import { motion } from "framer-motion";
import { Plane, Coffee, ShoppingBag, TrendingUp, ArrowRight } from "lucide-react";
import { LANDING } from "@/constants/testIds";

const LIFESTYLE = [
  { icon: Plane, label: "Travel & Lounge" },
  { icon: Coffee, label: "Lifestyle Cashback" },
  { icon: ShoppingBag, label: "Shopping Rewards" },
  { icon: TrendingUp, label: "Business Spends" },
];

export const CardDiscovery = () => {
  return (
    <section
      id="cards"
      data-testid={LANDING.cardDiscoverySection}
      className="relative py-24 md:py-32 bg-white border-y border-[#E5E5E0]"
    >
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="flex items-end justify-between gap-8 flex-wrap mb-14">
          <div className="max-w-2xl">
            <p className="text-[11px] uppercase tracking-[0.22em] font-semibold text-[#F25C2A]">
              · Compare &amp; apply credit cards
            </p>
            <h2 className="mt-4 font-serif text-4xl sm:text-5xl tracking-tight leading-[1.05] text-[#0A0A0A]">
              Discover the next card
              <br />
              that <em className="italic text-[#F25C2A] font-normal">fits your life</em>.
            </h2>
          </div>
          <p className="max-w-md text-[#4A4A4A] leading-relaxed">
            Compare offers across leading banks, match cards to your lifestyle
            and apply in minutes — entirely inside the EMDpay app.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Big card preview */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="lg:col-span-7 relative overflow-hidden rounded-3xl bg-[#0A0A0A] text-white p-8 sm:p-10 min-h-[360px]"
          >
            <div className="absolute -top-24 -right-24 h-72 w-72 rounded-full bg-[#F25C2A] opacity-30 blur-3xl" />
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#F25C2A]/15 text-[#F25C2A] text-[11px] uppercase tracking-[0.22em] font-semibold">
              <span className="h-1.5 w-1.5 rounded-full bg-[#F25C2A]" /> Get ₹500 Amazon Voucher
            </div>
            <h3 className="mt-5 font-serif text-3xl sm:text-4xl leading-snug max-w-md">
              Apply across <span className="text-[#F25C2A]">100+ credit cards</span> in three calm steps.
            </h3>

            <div className="mt-7 grid grid-cols-3 gap-3 text-xs font-semibold">
              {["Check Eligibility", "Select Card", "Apply Card"].map((step, i) => (
                <div
                  key={step}
                  className="rounded-2xl border border-white/10 bg-white/[0.04] p-3.5 flex items-center gap-2"
                >
                  <span
                    className={`inline-flex h-6 w-6 items-center justify-center rounded-full text-[10px] ${
                      i < 2
                        ? "bg-[#F25C2A] text-white"
                        : "border border-white/30 text-white/70"
                    }`}
                  >
                    {i < 2 ? "✓" : i + 1}
                  </span>
                  <span className="text-white/85 leading-tight">{step}</span>
                </div>
              ))}
            </div>

            <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-3">
              {[
                { name: "HDFC Infinia", reward: "3.6%" },
                { name: "Axis Magnus", reward: "4.0%" },
                { name: "ICICI Emeralde", reward: "4.4%" },
              ].map((card) => (
                <div
                  key={card.name}
                  className="relative aspect-[1.6/1] rounded-2xl overflow-hidden bg-gradient-to-br from-[#F25C2A] to-[#7a1f08] p-4 flex flex-col justify-between"
                >
                  <div className="flex justify-between items-start">
                    <p className="text-[10px] uppercase tracking-[0.18em] opacity-80">
                      Lifetime Free
                    </p>
                    <span className="text-[10px] font-semibold bg-white/15 backdrop-blur px-2 py-0.5 rounded-full">
                      {card.reward} reward
                    </span>
                  </div>
                  <div>
                    <p className="font-serif text-base leading-tight">{card.name}</p>
                    <p className="text-[10px] opacity-70 mt-0.5">Approval chance · 75%</p>
                  </div>
                </div>
              ))}
            </div>

            <a
              href="#top"
              className="mt-8 inline-flex items-center gap-2 text-sm font-semibold text-[#F25C2A] hover:text-white transition-colors"
            >
              Explore all cards <ArrowRight size={14} />
            </a>
          </motion.div>

          {/* Lifestyle filters */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1, duration: 0.6 }}
            className="lg:col-span-5 rounded-3xl bg-[#FAFAF7] border border-[#E5E5E0] p-8 sm:p-10"
          >
            <p className="text-[11px] uppercase tracking-[0.22em] font-semibold text-[#737373]">
              Match by lifestyle
            </p>
            <h3 className="mt-3 font-serif text-2xl sm:text-3xl leading-snug text-[#0A0A0A]">
              Pick what you spend on most.
            </h3>

            <div className="mt-7 grid grid-cols-2 gap-3">
              {LIFESTYLE.map((l) => (
                <div
                  key={l.label}
                  className="group bg-white border border-[#E5E5E0] rounded-2xl p-4 hover:border-[#F25C2A] transition-colors cursor-pointer"
                >
                  <l.icon size={18} className="text-[#F25C2A]" />
                  <p className="mt-3 text-sm font-semibold text-[#0A0A0A]">
                    {l.label}
                  </p>
                </div>
              ))}
            </div>

            <p className="mt-6 text-sm text-[#4A4A4A] leading-relaxed">
              We rank options on real reward value, not flashy badges.
              No hidden fees, no spam calls.
            </p>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
